import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    deactivateAccountMutationFn,
    deleteAccountMutationFn,
} from '@/lib/api';
import { toast } from '@/hooks/use-toast';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/context/auth-provider';
import { Checkbox } from '../ui/checkbox';
import {
    Field,
    FieldContent,
    FieldDescription,
    FieldGroup,
    FieldLabel,
} from '../ui/field';
import React from 'react';

const DestructiveActionsDialog = (props: {
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    action: string;
}) => {
    const { isOpen, setIsOpen, action } = props;
    const [checked, setChecked] = React.useState(false);
    const {
        user: { id, name },
    } = useAuthContext();
    const queryClient = useQueryClient();

    const navigate = useNavigate();

    const { mutate, isPending } = useMutation({
        mutationFn:
            action === 'delete'
                ? deleteAccountMutationFn
                : deactivateAccountMutationFn,
        onSuccess: (response) => {
            queryClient.resetQueries({
                queryKey: ['authUser'],
            });
            navigate('/');
            setIsOpen(false);
            toast({
                title:
                    action === 'delete'
                        ? `Goodbye ${name}!`
                        : 'We will miss you!',
                description: response.data.message,
            });
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            });
        },
    });

    const handleDeleteAccount = () => {
        mutate({ userId: id });
    };

    const handleDialogOpen = () => {
        setChecked(false);
        setIsOpen((prev) => !prev);
    };

    return (
        <Dialog
            open={isOpen}
            onOpenChange={handleDialogOpen}>
            <DialogContent className='rounded-md w-[90%] max-w-lg'>
                <DialogHeader>
                    <DialogTitle className='text-sm md:text-lg'>
                        Are you sure you want to{' '}
                        {action === 'delete' ? 'delete' : 'deactivate'} your
                        account?
                    </DialogTitle>
                    <DialogDescription className='text-[0.75rem]'>
                        {action === 'delete' ? (
                            <>
                                This action will permanently{' '}
                                <span className='text-destructive font-bold uppercase'>
                                    delete
                                </span>{' '}
                                your account and your data, and can{' '}
                                <span className='font-bold text-destructive'>
                                    NOT
                                </span>{' '}
                                be undone.
                            </>
                        ) : (
                            <>
                                This action will{' '}
                                <span className='text-destructive font-bold uppercase'>
                                    deactivate
                                </span>{' '}
                                your account, but can be undone by login to your
                                account and satisfying the requirements.
                            </>
                        )}
                    </DialogDescription>
                </DialogHeader>

                <div className='w-full mt-6'>
                    <FieldGroup className='w-72'>
                        <Field
                            orientation='horizontal'
                            data-invalid>
                            <Checkbox
                                checked={checked}
                                onCheckedChange={(e) =>
                                    setChecked(e.valueOf() as boolean)
                                }
                                className='border-destructive aria-checked:border-primary'
                                id='terms-checkbox-desc'
                                name='terms-checkbox-desc'
                                aria-invalid
                            />
                            <FieldContent>
                                <FieldLabel htmlFor='terms-checkbox-desc'>
                                    I understand and agree
                                </FieldLabel>
                                <FieldDescription>
                                    By clicking this checkbox, you agree to the
                                    terms and conditions.
                                </FieldDescription>
                            </FieldContent>
                        </Field>
                    </FieldGroup>
                </div>

                <DialogFooter className='flex flex-col md:flex-row gap-y-4'>
                    <Button
                        disabled={isPending}
                        type='button'
                        variant={'outline'}
                        onClick={handleDialogOpen}>
                        {isPending && <Loader className='animate-spin' />}
                        Cancel
                    </Button>
                    <Button
                        disabled={isPending || !checked}
                        type='button'
                        variant={'destructive'}
                        className='bg-destructive/15 text-destructive hover:text-foreground'
                        onClick={handleDeleteAccount}>
                        {isPending && <Loader className='animate-spin' />}
                        Proceed
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default DestructiveActionsDialog;
